/**
 * 大六壬 · 三传（九宗门）
 *
 * 判定次序（依《六壬》通行九宗门）：
 *   伏吟（月将＝占时） / 返吟（月将冲占时）  → 先按贼克法，无克则用本门专法
 *   常规：贼克（重审 / 元首） → 比用（比用 / 知一） → 涉害（涉害 / 见机 / 察微 / 复等）
 *        → 遥克（蒿矢 / 弹射） → 昴星（虎视 / 冬蛇掩目）
 *        → 别责（四课只三课） → 八专（干支同宫，只两课）
 *
 * 「上神」取法：地盘第 i 宫固定为地支 i，故下神 z 之上神 = tianPan[z]。
 * 三传通例：中传取初传之上神，末传取中传之上神（伏吟 / 昴星 / 别责 / 八专 / 返吟无克另有专法）。
 *
 * 说明：涉害深浅、八专顺逆数神等细节，六壬诸家略有异说；本实现采用以下并在注释中明示的通行取法，
 * 便于日后按实际校验结果替换。
 */
import { chong, ganHe, ganWuXing, isSelfXing, isYangGan, isYangZhi, jiGong, ke, mengZhongJi, xing, zhiOf, zhiWuXing, } from './constants';
/** 下神五行索引 */
function xiaWuXing(k) {
    return k.xiaType === 'gan' ? ganWuXing(k.xiaIdx) : zhiWuXing(k.xiaIdx);
}
export function computeSanChuan(input) {
    const { tianPanIdx, dayGanIndex, dayZhiIndex, yueJiangIndex, zhanShiIndex, ke: kes } = input;
    /** 某支之上神（该支固定居于地盘同宫） */
    const upper = (z) => tianPanIdx[z];
    /** 某天盘支所临地盘宫位 */
    const palace = (b) => (((zhanShiIndex + b - yueJiangIndex) % 12) + 12) % 12;
    /** 四课「下贼上」（下神克上神） */
    const zeiUp = [];
    /** 四课「上克下」（上神克下神） */
    const keDown = [];
    kes.forEach((k, i) => {
        if (ke(xiaWuXing(k), zhiWuXing(k.shangIdx)))
            zeiUp.push(i);
        else if (ke(zhiWuXing(k.shangIdx), xiaWuXing(k)))
            keDown.push(i);
    });
    /** 涉害深浅：从天盘神所临地盘宫顺行数至其本家，统计途中被地盘克制的宫数 */
    function sheHaiDepth(b) {
        let n = 0;
        let p = palace(b);
        for (let guard = 0; guard < 12; guard += 1) {
            if (ke(zhiWuXing(p), zhiWuXing(b)))
                n += 1;
            if (p === b)
                break;
            p = (p + 1) % 12;
        }
        return n;
    }
    /**
     * 涉害取用（通行取法）：
     * 1. 涉害最深处唯一者即为初传 —— 涉害课；
     * 2. 深浅相同者，取四孟上神为用（唯一时）—— 见机课；
     * 3. 无孟（或孟上不止一神）取四仲上神 —— 察微课；
     * 4. 无仲取四季上神 —— 复等课；
     * 5. 孟仲季皆不能定，取与日干比（同阴阳）者，再不能定取候选之首。
     */
    function pickSheHai(cands) {
        const depths = cands.map((c) => sheHaiDepth(c.shangIdx));
        const max = Math.max(...depths);
        const deepPool = cands.filter((_, i) => depths[i] === max);
        if (deepPool.length === 1)
            return { idx: deepPool[0].shangIdx, name: '涉害课', school: '涉害' };
        const names = { 孟: '见机课', 仲: '察微课', 季: '复等课' };
        for (const cls of ['孟', '仲', '季']) {
            const sub = deepPool.filter((c) => mengZhongJi(palace(c.shangIdx)) === cls);
            if (sub.length === 1) {
                return { idx: sub[0].shangIdx, name: names[cls], school: '涉害' };
            }
        }
        const same = deepPool.filter((c) => isYangZhi(c.shangIdx) === isYangGan(dayGanIndex));
        const chosen = (same.length > 0 ? same : deepPool)[0];
        return { idx: chosen.shangIdx, name: '涉害课', school: '涉害' };
    }
    /** 比用：取与日干同阴阳者；仍多于一个则转涉害 */
    function pickBiYong(cands, biYongName, biYongSchool) {
        if (cands.length === 1)
            return { idx: cands[0].shangIdx, name: biYongName, school: biYongSchool };
        const same = cands.filter((c) => isYangZhi(c.shangIdx) === isYangGan(dayGanIndex));
        if (same.length === 1)
            return { idx: same[0].shangIdx, name: biYongName, school: biYongSchool };
        return pickSheHai(same.length > 1 ? same : cands);
    }
    /** 三传通例：中传取初传上神，末传取中传上神 */
    const chain = (first) => {
        const mid = upper(first);
        return [first, mid, upper(mid)];
    };
    // ════════════ 伏吟课（月将 ＝ 占时） ════════════
    if (yueJiangIndex === zhanShiIndex) {
        let chuan;
        let name;
        let reason;
        let school = '伏吟';
        if (zeiUp.length > 0 || keDown.length > 0) {
            const list = zeiUp.length > 0 ? zeiUp : keDown;
            const cands = list.map((i) => kes[i]);
            const picked = pickBiYong(cands, zeiUp.length > 0 ? '比用课' : '知一课', '比用');
            chuan = [picked.idx, 0, 0];
            name = '不虞课';
            reason = `月将(${zhiOf(yueJiangIndex)})与占时(${zhiOf(zhanShiIndex)})同宫为伏吟；四课有${zeiUp.length > 0 ? '下贼上' : '上克下'}，照贼克法以${picked.name}取${zhiOf(picked.idx)}为初传`;
        }
        else {
            const first = isYangGan(dayGanIndex) ? upper(jiGong(dayGanIndex)) : upper(dayZhiIndex);
            chuan = [first, 0, 0];
            name = isYangGan(dayGanIndex) ? '自任课' : '自信课';
            reason = `月将(${zhiOf(yueJiangIndex)})与占时(${zhiOf(zhanShiIndex)})同宫为伏吟；四课无贼无克，${isYangGan(dayGanIndex) ? '阳日取干上神' : '阴日取支上神'}为初传`;
        }
        // 中传取初传所刑（初传为自刑则取另一课之上神），末传取中传所刑（中传自刑则取其冲）
        const first = chuan[0];
        const mid = isSelfXing(first)
            ? isYangGan(dayGanIndex)
                ? upper(dayZhiIndex)
                : upper(jiGong(dayGanIndex))
            : xing(first);
        const last = isSelfXing(mid) ? chong(mid) : xing(mid);
        chuan = [first, mid, last];
        school = '伏吟';
        return withExtras({ chuan, school, keTiName: `伏吟课（${name}）`, reason });
    }
    // ════════════ 返吟课（月将冲占时） ════════════
    if ((yueJiangIndex + 6) % 12 === zhanShiIndex) {
        const extra = `月将(${zhiOf(yueJiangIndex)})冲占时(${zhiOf(zhanShiIndex)})为返吟；`;
        if (zeiUp.length > 0 || keDown.length > 0) {
            const list = zeiUp.length > 0 ? zeiUp : keDown;
            const picked = pickBiYong(list.map((i) => kes[i]), zeiUp.length > 0 ? '比用课' : '知一课', '比用');
            return withExtras({
                chuan: chain(picked.idx),
                school: '返吟',
                keTiName: '返吟课（无依）',
                reason: `${extra}四课有${zeiUp.length > 0 ? '下贼上' : '上克下'}，照贼克法以${picked.name}取${zhiOf(picked.idx)}为初传，中末传依上神递取`,
            });
        }
        const yang = isYangGan(dayGanIndex);
        const first = yang ? upper(dayZhiIndex) : upper(jiGong(dayGanIndex));
        const mid = chong(first);
        const last = yang ? upper(jiGong(dayGanIndex)) : upper(dayZhiIndex);
        return withExtras({
            chuan: [first, mid, last],
            school: '返吟',
            keTiName: '返吟课（无亲）',
            reason: `${extra}四课无克，${yang ? '阳日取支上神' : '阴日取干上神'}为初传，中传取初传之冲，末传取${yang ? '干上神' : '支上神'}`,
        });
    }
    // ════════════ 贼克法（重审 / 元首） ════════════
    if (zeiUp.length > 0 || keDown.length > 0) {
        const isZei = zeiUp.length > 0;
        const list = isZei ? zeiUp : keDown;
        const cands = list.map((i) => kes[i]);
        const picked = pickBiYong(cands, isZei ? '比用课' : '知一课', '比用');
        if (cands.length === 1) {
            const nm = isZei ? '重审课' : '元首课';
            return withExtras({
                chuan: chain(picked.idx),
                school: '贼克',
                keTiName: nm,
                reason: `四课中唯${isZei ? `第${list[0] + 1}课下贼上` : `第${list[0] + 1}课上克下`}，取其上神${zhiOf(picked.idx)}为初传`,
            });
        }
        return withExtras({
            chuan: chain(picked.idx),
            school: picked.school,
            keTiName: picked.name,
            reason: `四课有 ${cands.length} 处${isZei ? '下贼上' : '上克下'}，以${picked.school === '涉害' ? '涉害深浅' : '与日干比者'}取用，得${zhiOf(picked.idx)}为初传`,
        });
    }
    // ════════════ 遥克法（蒿矢 / 弹射） ════════════
    const me = ganWuXing(dayGanIndex);
    const haoShi = kes.filter((k) => ke(zhiWuXing(k.shangIdx), me)); // 上神克日干
    const tanShe = kes.filter((k) => ke(me, zhiWuXing(k.shangIdx))); // 日干克上神
    if (haoShi.length > 0 || tanShe.length > 0) {
        const isHao = haoShi.length > 0;
        const cands = isHao ? haoShi : tanShe;
        const picked = pickBiYong(cands, isHao ? '蒿矢课' : '弹射课', '遥克');
        return withExtras({
            chuan: chain(picked.idx),
            school: '遥克',
            keTiName: isHao ? '蒿矢课' : '弹射课',
            reason: `四课无贼无克，${isHao ? '取上神克日干者（遥克）' : '取日干克上神者（遥克）'}，以${picked.school === '涉害' ? '涉害' : '比用'}取${zhiOf(picked.idx)}为初传`,
        });
    }
    // ════════════ 结构判定：八专 / 别责 / 昴星 ════════════
    const shangList = kes.map((k) => k.shangIdx);
    const distinct = new Set(shangList).size;
    const ganGong = jiGong(dayGanIndex);
    // 八专：干寄宫与日支同宫（四课只得两课）
    if (ganGong === dayZhiIndex) {
        const yang = isYangGan(dayGanIndex);
        const ganShang = upper(ganGong);
        // 阳日：自干上神顺数三神（含本神）；阴日：自支阴神（第四课上神）逆数三神
        const first = yang ? (ganShang + 2) % 12 : (kes[3].shangIdx - 2 + 12) % 12;
        return withExtras({
            chuan: [first, ganShang, ganShang],
            school: '八专',
            keTiName: '八专课',
            reason: `日干${zhiOf(ganGong)}寄宫与日支同宫、四课只得两课为八专；${yang ? '阳日自干上神顺数三神' : '阴日自支阴神逆数三神'}得${zhiOf(first)}为初传，中末传皆取干上神`,
        });
    }
    // 别责：四课不全（只三课）
    if (distinct === 3) {
        const yang = isYangGan(dayGanIndex);
        const ganShang = upper(ganGong);
        const first = yang ? upper(jiGong(ganHe(dayGanIndex))) : upper((dayZhiIndex + 4) % 12);
        return withExtras({
            chuan: [first, ganShang, ganShang],
            school: '别责',
            keTiName: '别责课',
            reason: `四课只得三课、无贼无克无遥克为别责；${yang ? '阳日取干合之神上神' : '阴日取支前三合上神'}得${zhiOf(first)}为初传，中末传皆取干上神`,
        });
    }
    // 昴星：四课齐备而无贼无克无遥克
    {
        const yang = isYangGan(dayGanIndex);
        const ganShang = upper(ganGong);
        const zhiShang = upper(dayZhiIndex);
        const YOU = 9; // 酉
        // 阳日取地盘酉上神，阴日取天盘酉下神（酉所临地盘宫）
        const first = yang ? tianPanIdx[YOU] : palace(YOU);
        return withExtras({
            chuan: yang ? [first, zhiShang, ganShang] : [first, ganShang, zhiShang],
            school: '昴星',
            keTiName: yang ? '昴星课（虎视）' : '昴星课（冬蛇掩目）',
            reason: `四课齐备而无贼无克无遥克为昴星；${yang ? '阳日取地盘酉上神' : '阴日取天盘酉下神'}得${zhiOf(first)}为初传，中传取${yang ? '支上神' : '干上神'}，末传取${yang ? '干上神' : '支上神'}`,
        });
    }
}
/** 汇总附加课体（连茹 / 三合 / 递生递克 / 俱阳俱阴） */
function withExtras(base) {
    const [a, b, c] = base.chuan;
    const extras = [];
    // 连茹（三传地支相连）
    if ((b === (a + 1) % 12 && c === (a + 2) % 12))
        extras.push('进连茹');
    else if ((b === (a - 1 + 12) % 12 && c === (a - 2 + 12) % 12))
        extras.push('退连茹');
    // 三合局
    const ju = new Set([a, b, c]);
    if (ju.size === 3) {
        for (const group of [[8, 0, 4], [11, 3, 7], [2, 6, 10], [5, 9, 1]]) {
            if (group.every((g) => ju.has(g))) {
                extras.push('三合课');
                break;
            }
        }
    }
    // 递生 / 递克
    const wx = [zhiWuXing(a), zhiWuXing(b), zhiWuXing(c)];
    const sheng = (x, y) => (x + 1) % 5 === y;
    const keR = (x, y) => (x + 2) % 5 === y;
    if (sheng(wx[0], wx[1]) && sheng(wx[1], wx[2]))
        extras.push('递生');
    if (keR(wx[0], wx[1]) && keR(wx[1], wx[2]))
        extras.push('递克');
    // 俱阳 / 俱阴
    const allYang = [a, b, c].every((z) => z % 2 === 0);
    const allYin = [a, b, c].every((z) => z % 2 === 1);
    if (allYang)
        extras.push('三传俱阳');
    if (allYin)
        extras.push('三传俱阴');
    return { ...base, extras };
}
//# sourceMappingURL=sanChuan.js.map